import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./partners.css";
import { Partner, PartnerContacts, partners } from "./data";

function ChevronLeft() {
  return (
    <svg width="11" height="19" viewBox="0 0 11 19" fill="none">
      <path d="M9 2 2 9.5 9 17" stroke="#2A241F" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GiftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M20 7H4v4h16V7Z" stroke="#F7EFE7" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M5 11v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8M12 7v13" stroke="#F7EFE7" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M12 7S10.5 3.5 8.3 3.5c-1.2 0-2 .9-2 1.9 0 1 .8 1.6 2 1.6H12Zm0 0s1.5-3.5 3.7-3.5c1.2 0 2 .9 2 1.9 0 1-.8 1.6-2 1.6H12Z" stroke="#F7EFE7" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 11.3 19.79 19.79 0 0 1 1.04 2.72a2 2 0 0 1 1.99-2.19h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 5.5 5.5l1.77-1.77a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function InstaIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="12" cy="12" r="9.2" stroke="#998C7E" strokeWidth="1.7" />
      <path d="M12 11v6M12 7.6v.1" stroke="#998C7E" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

function Header({ onBack, canBack }: { onBack?: () => void; canBack: boolean }) {
  return (
    <div className="p-hdr">
      <button
        className="p-hdr__back"
        style={{ visibility: canBack ? "visible" : "hidden" }}
        onClick={onBack}
        aria-label="Назад"
      >
        <ChevronLeft />
      </button>
      <div className="p-hdr__title">Партнёры <b>AMANI</b></div>
    </div>
  );
}

function PartnerCard({ partner, onClick }: { partner: Partner; onClick: () => void }) {
  return (
    <button className="p-card" onClick={onClick}>
      <div className="p-card__img-wrap">
        <img className="p-card__img" src={partner.image} alt={partner.name} loading="lazy" />
      </div>
      <div className="p-card__body">
        <div className="p-card__text">
          <div className="p-card__name">{partner.name}</div>
          <div className="p-card__cat">{partner.category}</div>
        </div>
        {(partner.benefitLabel || partner.benefitShort) && (
          <span className="p-badge">
            <span className="p-badge__dot" />
            {partner.benefitLabel}{partner.benefitShort ? ` ${partner.benefitShort}` : ""}
          </span>
        )}
      </div>
    </button>
  );
}

function ContactButtons({ contacts }: { contacts: PartnerContacts }) {
  const items: { href: string; label: string; icon: React.ReactNode }[] = [];

  if (contacts.phone) {
    items.push({
      href: `tel:${contacts.phone.replace(/\s/g, "")}`,
      label: contacts.phone,
      icon: <PhoneIcon />,
    });
  }
  if (contacts.website) {
    items.push({
      href: `https://${contacts.website}`,
      label: contacts.website,
      icon: <GlobeIcon />,
    });
  }
  if (contacts.instagram) {
    items.push({
      href: `https://www.instagram.com/${contacts.instagram}/`,
      label: `@${contacts.instagram}`,
      icon: <InstaIcon />,
    });
  }
  if (contacts.address) {
    items.push({
      href: `https://yandex.by/maps/?text=${encodeURIComponent(`Минск, ${contacts.address}`)}`,
      label: contacts.address,
      icon: <PinIcon />,
    });
  }

  if (!items.length) return null;

  return (
    <div className="p-sec">
      <div className="p-sec__h">Контакты</div>
      <div className="p-contacts">
        {items.map((item) => (
          <a key={item.href} className="p-contact-btn" href={item.href} target="_blank" rel="noopener noreferrer">
            {item.icon}
            <span>{item.label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

function PartnerDetail({ partner, open, onBack }: { partner: Partner | null; open: boolean; onBack: () => void }) {
  return (
    <div className={`partners__screen partners__detail-screen${open ? " is-open" : ""}`}>
      <Header canBack onBack={onBack} />
      {partner && (
        <div className="p-detail" key={partner.id}>
          <div className="p-detail__hero">
            <img className="p-detail__hero-img" src={partner.image} alt={partner.name} />
          </div>
          <div className="p-detail__pad">
            <p className="p-detail__cat">{partner.category}</p>
            <h2 className="p-detail__name">{partner.name}</h2>

            <div className="p-offer">
              <div className="p-offer__ic"><GiftIcon /></div>
              <div>
                {partner.benefitLabel && <div className="p-offer__label">{partner.benefitLabel}</div>}
                <div className="p-offer__text">{partner.offer}</div>
              </div>
            </div>

            <div className="p-sec">
              <div className="p-sec__h">О партнёре</div>
              <p className="p-about">{partner.details}</p>
            </div>

            <ContactButtons contacts={partner.contacts} />

            <div className="p-note">
              <InfoIcon />
              <span>
                Для получения скидки предъявите партнёру действующий абонемент AMANI и скриншот этой страницы.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PartnersPage() {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState<string | null>(null);
  const [current, setCurrent] = useState<Partner | null>(null);

  const open = useCallback((id: string) => {
    setCurrent(partners.find((p) => p.id === id) ?? null);
    setOpenId(id);
  }, []);

  const closeDetail = useCallback(() => {
    setOpenId(null);
    setTimeout(() => setCurrent(null), 440);
  }, []);

  return (
    <div className={`partners${openId ? " partners--detail-open" : ""}`} data-joy-color-scheme="light">
      <div className="partners__screen partners__list-screen">
        <Header canBack onBack={() => navigate(-1)} />
        <div className="p-list">
          <h1 className="p-list__lede">Привилегии для своих</h1>
          <p className="p-list__sub">Скидки и сертификаты от партнёров AMANI.</p>
          <div className="p-grid">
            {partners.map((p) => (
              <PartnerCard key={p.id} partner={p} onClick={() => open(p.id)} />
            ))}
          </div>
        </div>
      </div>
      <PartnerDetail partner={current} open={!!openId} onBack={closeDetail} />
    </div>
  );
}
